import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import BrandName from '../components/BrandName';
import api from '../services/api';

class ResetPassword extends Component {
  constructor(props) {
    super(props);
    this.state = {
      token: '',
      password: '',
      confirmPassword: '',
      errors: {},
      isSubmitting: false,
      isValidating: true,
      tokenValid: false,
      resetSuccess: false,
      error: null,
      email: '',
    };
  }

  componentDidMount() {
    // Get token from URL query params
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (!token) {
      this.setState({ 
        isValidating: false, 
        tokenValid: false,
        error: 'Invalid reset link'
      });
      return;
    }

    this.setState({ token }, () => {
      this.validateToken(token);
    });
  }

  validateToken = async (token) => {
    try {
      const response = await api.get(`/password-reset/validate/${token}`);
      
      if (response.data.valid) {
        this.setState({ 
          isValidating: false,
          tokenValid: true,
          email: response.data.email
        });
      } else {
        this.setState({ 
          isValidating: false,
          tokenValid: false,
          error: 'This reset link is invalid or has expired'
        });
      }
    } catch (error) {
      this.setState({ 
        isValidating: false,
        tokenValid: false,
        error: 'This reset link is invalid or has expired'
      });
    }
  };

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState({
      [name]: value,
      errors: { ...this.state.errors, [name]: '' },
      error: null,
    });
  };

  validateForm = () => {
    const { password, confirmPassword } = this.state;
    const errors = {};

    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      errors.password = 'Password must contain uppercase, lowercase, and number';
    }

    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    return errors;
  };

  handleSubmit = async (e) => {
    e.preventDefault();

    const errors = this.validateForm();
    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      return;
    }

    this.setState({ isSubmitting: true, error: null });

    try {
      const { token, password } = this.state;
      await api.post('/password-reset/reset', { token, password });
      
      this.setState({ 
        isSubmitting: false,
        resetSuccess: true 
      });
    } catch (error) {
      this.setState({
        isSubmitting: false,
        error: error.response?.data?.message || 'Failed to reset password. Please try again.',
      });
    }
  };

  render() {
    const { password, confirmPassword, errors, isSubmitting, isValidating, tokenValid, resetSuccess, error } = this.state;

    // Loading state
    if (isValidating) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <div className="card text-center">
              <LoadingSpinner size="large" />
              <p className="mt-4 text-slate-600">Validating reset link...</p>
            </div>
          </div>
        </div>
      );
    }

    // Invalid token
    if (!tokenValid) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <div className="card animate-fade-in">
              <div className="flex justify-center mb-8">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              </div>

              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-slate-800 mb-2">Invalid Link</h1>
                <p className="text-slate-600 mb-4">{error}</p>
                <p className="text-sm text-slate-500">
                  Password reset links expire after 1 hour. Please request a new one.
                </p>
              </div>

              <Link 
                to="/forgot-password"
                className="btn-primary w-full block text-center"
              >
                Request new link
              </Link>

              <div className="mt-4 text-center">
                <Link to="/login" className="text-sm text-slate-600 hover:text-slate-900">
                  Back to login
                </Link>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Success state
    if (resetSuccess) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <div className="card animate-fade-in">
              <div className="flex justify-center mb-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>

              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-slate-800 mb-2">Password reset!</h1>
                <p className="text-slate-600">
                  Your password has been successfully reset. You can now log in with your new password.
                </p>
              </div>

              <Link 
                to="/login"
                className="btn-primary w-full block text-center"
              >
                Continue to login
              </Link>
            </div>
          </div>
        </div>
      );
    }

    // Reset password form
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="card animate-fade-in">
            <div className="flex justify-center mb-8">
              <img 
                src="/logo.png" 
                alt="PlansSource Logo" 
                className="w-32 h-32 object-contain"
              />
            </div>

            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-slate-800 mb-2">Set new password</h1>
              <p className="text-slate-500">
                Enter a new password for your account
              </p>
            </div>

            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <form onSubmit={this.handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2">
                  New password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={this.handleChange}
                  className={`input-field ${errors.password ? 'border-red-500' : ''}`}
                  placeholder="••••••••"
                  disabled={isSubmitting}
                  autoFocus
                />
                {errors.password && <p className="error-text">{errors.password}</p>}
                <p className="mt-1 text-xs text-slate-500">
                  Must be at least 8 characters with uppercase, lowercase, and number
                </p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700 mb-2">
                  Confirm password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={this.handleChange}
                  className={`input-field ${errors.confirmPassword ? 'border-red-500' : ''}`}
                  placeholder="••••••••"
                  disabled={isSubmitting}
                />
                {errors.confirmPassword && <p className="error-text">{errors.confirmPassword}</p>}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <LoadingSpinner size="small" />
                    <span className="ml-2">Resetting password...</span>
                  </span>
                ) : (
                  'Reset password'
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/login" className="text-sm text-slate-600 hover:text-slate-900">
                Back to login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default ResetPassword;